import { workManagementService } from '@/utils/workManagementService'
import { csIssueRepository } from '@/views/CSIssueReport/cs-issue-repository'
import { shouldUseDummyNotificationSource } from '@/policies/dummy-feature-visibility-policy.mjs'

import {
  normalizeCsNotification,
  normalizeSecurityNotification,
  normalizeSystemNotification,
  normalizeWorkNotification,
  paginateLocalNotifications,
} from './notification-center-domain.mjs'
import {
  getAllSecurityAlertNotifications,
  getSecurityAlertNotifications,
  markAllSecurityAlertsRead,
  markSecurityAlertRead,
} from './security-alert-api'
import { systemNotificationRepository } from './system-notification-repository'

function pageResult(items, meta = {}, fallback = {}) {
  const total = Number(meta.total ?? meta.count ?? items.length)
  const pageSize = Number(meta.limit ?? meta.pageSize ?? fallback.pageSize ?? 30)
  const page = Number(meta.page ?? fallback.page ?? 1)
  return {
    items,
    total,
    page,
    maxPage: Number(meta.maxPage ?? meta.max_page ?? Math.max(1, Math.ceil(total / pageSize))),
    pageSize,
  }
}

const sourceAdapters = {
  security: {
    async loadPage(options) {
      const data = await getSecurityAlertNotifications(options)
      return pageResult(data.results.map(normalizeSecurityNotification), { count: data.count }, options)
    },
    async getSummary() {
      const items = (await getAllSecurityAlertNotifications()).map(normalizeSecurityNotification)
      return { items, unreadCount: items.filter(item => !item.isRead).length }
    },
    markRead(item) {
      return markSecurityAlertRead(item.id)
    },
    markAllRead() {
      return markAllSecurityAlertsRead()
    },
  },
  work: {
    async loadPage(options) {
      const result = await workManagementService.getWorkNotificationsWithMeta({
        page: options.page,
        limit: options.pageSize,
      })
      return pageResult(result.data.map(normalizeWorkNotification), result.meta, options)
    },
    async getSummary() {
      const [result, unreadCount] = await Promise.all([
        workManagementService.getWorkNotificationsWithMeta({ page: 1, limit: 5, unread: true }),
        workManagementService.getNotificationUnreadCount(),
      ])
      return { items: result.data.map(normalizeWorkNotification), unreadCount }
    },
    markRead(item) {
      return workManagementService.markNotificationRead(item.id)
    },
  },
  cs: {
    loadPage(options, context) {
      csIssueRepository.initializeRole(context.permission)
      const items = csIssueRepository.getNotifications().map(normalizeCsNotification)
      return Promise.resolve(paginateLocalNotifications(items, options))
    },
    getSummary(context) {
      csIssueRepository.initializeRole(context.permission)
      const items = csIssueRepository.getNotifications().map(normalizeCsNotification)
      return Promise.resolve({ items, unreadCount: items.filter(item => !item.isRead).length })
    },
    markRead(item) {
      return Promise.resolve(csIssueRepository.markNotificationRead(item.id))
    },
  },
  system: {
    loadPage(options) {
      return Promise.resolve(paginateLocalNotifications(
        systemNotificationRepository.list().map(normalizeSystemNotification),
        options,
      ))
    },
    getSummary() {
      const items = systemNotificationRepository.list().map(normalizeSystemNotification)
      return Promise.resolve({ items, unreadCount: items.filter(item => !item.isRead).length })
    },
    markRead(item) {
      return Promise.resolve(systemNotificationRepository.markRead(item.id))
    },
  },
}

export async function loadNotificationPage(source, options, context = {}) {
  const adapter = sourceAdapters[source]
  if (!adapter) throw new Error(`Unsupported notification source: ${source}`)
  if (!shouldUseDummyNotificationSource(source, process.env.NODE_ENV)) {
    return pageResult([], {}, options)
  }
  return adapter.loadPage(options, context)
}

export async function loadNotificationSummary(source, context = {}) {
  const adapter = sourceAdapters[source]
  if (!adapter) throw new Error(`Unsupported notification source: ${source}`)
  if (!shouldUseDummyNotificationSource(source, process.env.NODE_ENV)) {
    return { items: [], unreadCount: 0 }
  }
  return adapter.getSummary(context)
}

export async function markNotificationRead(item) {
  const adapter = sourceAdapters[item.source]
  if (!adapter) throw new Error(`Unsupported notification source: ${item.source}`)
  return adapter.markRead(item)
}

export async function markAllNotificationsRead(source) {
  const adapter = sourceAdapters[source]
  if (!adapter?.markAllRead) throw new Error(`Unsupported notification source for mark all read: ${source}`)
  return adapter.markAllRead()
}

export async function resolveNotificationTarget(item) {
  if (!item?.target) return null
  if (item.target.kind === 'route') return item.target.location
  if (item.target.kind !== 'work') return null

  let projectId = null
  let workId = item.target.workId
  if (workId) {
    const context = await workManagementService.getWorkRouteContext(workId)
    projectId = context.projectId
  } else if (item.target.workItemId) {
    const workItem = await workManagementService.getWorkItem(item.target.workItemId)
    workId = workItem?.work_id || workItem?.work?.id
    projectId = workItem?.project_id || workItem?.work?.project_id
  }
  if (!projectId || (!workId && !item.target.workItemId)) {
    const error = new Error('알림의 상세 화면을 찾을 수 없습니다.')
    error.code = 'NOTIFICATION_TARGET_NOT_FOUND'
    throw error
  }
  if (item.target.workItemId) {
    return {
      name: 'WorkItemRedirect',
      params: { pk: projectId, work_item_id: item.target.workItemId },
    }
  }
  return { name: 'WorkDetail', params: { pk: projectId, work_id: workId } }
}

export function resetDummyNotificationSources(permission) {
  csIssueRepository.resetForUser(permission)
  systemNotificationRepository.reset()
}
